import { computed, onBeforeUnmount, onMounted, ref, toValue, unref, watch } from 'vue';

import { useEventHub } from '@satellite/composables/useEventHub';
import yardSpotService from '@satellite/services/yardSpot-service';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe, Nullable } from '@nova/core';

export interface YardSpot {
  id?: Maybe<string>;
  code?: Nullable<string>;
  spotAreaId?: Maybe<string>;
  type?: Nullable<string>;
}

export interface UseYardSpotPresenterOptions {
  spotId?: Ref<Maybe<string>>;
  /**
   * Initial spot data (already fetched). Used only for initial state.
   * If live=true, this will be refetched and overridden.
   */
  spot?: Ref<Maybe<YardSpot>> | ComputedRef<Maybe<YardSpot>> | Maybe<YardSpot>;
  /**
   * If true, fetches immediately when component mounts or when spotId changes.
   * If false, defers fetching until component enters viewport (requires v-intersect).
   * Default: true
   */
  immediate?: boolean;
  /**
   * If true, the spot will be refreshed automatically when the spot is updated (via eventHub).
   */
  live?: boolean;
}

export function useYardSpotPresenter(options: UseYardSpotPresenterOptions = {}) {
  const { spotId, spot, immediate = true, live = false } = options;
  const eventHub = useEventHub();

  const fetchedSpot = ref<Nullable<YardSpot>>(null);
  const isLoading = ref(false);
  const needsFetch = ref(false);
  // When immediate=true, we don't need viewport tracking - always consider it "in viewport"
  // When immediate=false, start as false and let v-intersect update it
  const isInViewport = ref(immediate);

  const effectiveSpotId = computed((): Maybe<string> => {
    const explicitSpotId = unref(spotId);

    if (explicitSpotId) {
      const initialSpotId = toValue(spot)?.id;

      if (initialSpotId && initialSpotId !== explicitSpotId) {
        console.warn(
          '[useYardSpotPresenter] spotId mismatch:',
          `explicit spotId (${explicitSpotId}) does not match spot.id (${initialSpotId}). Using explicit spotId.`
        );
      }

      return explicitSpotId;
    }

    return toValue(spot)?.id || null;
  });

  const currentSpot = computed(() => {
    return {
      ...toValue(spot),
      ...fetchedSpot.value
    };
  });

  const spotCode = computed((): Nullable<string> => {
    if (!currentSpot.value) {
      return null;
    }

    return currentSpot.value.code || null;
  });

  const areaId = computed((): Maybe<string> => {
    if (!currentSpot.value) {
      return null;
    }

    return currentSpot.value.spotAreaId || null;
  });

  const fetchSpot = async (id: string): Promise<void> => {
    if (!id) {
      fetchedSpot.value = null;
      return;
    }

    isLoading.value = true;
    needsFetch.value = false;

    try {
      const response = await yardSpotService.getOneById(id);
      fetchedSpot.value = response;
    } catch (err) {
      console.error('[useYardSpotPresenter] Failed to fetch spot:', err);

      const error = err instanceof Error ? err : new Error(String(err));

      fetchedSpot.value = null;

      // throw error for callers that await
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const refresh = async (onError?: (error: Error) => void): Promise<void> => {
    const id = effectiveSpotId.value;
    if (!id) {
      fetchedSpot.value = null;
      needsFetch.value = false;
      return;
    }

    // Only check viewport when immediate=false (used in components with v-intersect)
    // When immediate=true, fetch immediately regardless of viewport
    if (!immediate && !isInViewport.value) {
      needsFetch.value = true;
      return;
    }

    try {
      needsFetch.value = false;
      await fetchSpot(id);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[useYardSpotPresenter] Failed to refresh spot:', error);

      if (onError) {
        onError(error);
      }

      // throw error for callers that await
      throw error;
    }
  };

  const handleIntersect = async (entry?: { isIntersecting?: boolean } | boolean) => {
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;

    isInViewport.value = isIntersecting;

    // If component is in viewport and we have a pending fetch or live=true, refresh to ensure fresh data
    // This handles cases where events arrived while component was not visible
    if (isIntersecting && (needsFetch.value || live)) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }
  };

  const handleUpdateSpotEvent = async (event: { id: Nullable<string> }) => {
    if (event?.id === effectiveSpotId.value) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }
  };

  watch(
    () => unref(spotId),
    async newId => {
      if (newId) {
        fetchedSpot.value = null;
        // Fire-and-forget for automatic initial fetch
        try {
          await refresh();
        } catch {
          // Error already logged in refresh()
        }
      } else {
        fetchedSpot.value = null;
        needsFetch.value = false;
      }
    }
  );

  onMounted(async () => {
    const hasCode = Boolean(spotCode.value);

    if (immediate || !hasCode) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }

    if (live) {
      eventHub.on('update-Spot', handleUpdateSpotEvent);
    }
  });

  onBeforeUnmount(() => {
    if (live) {
      eventHub.off('update-Spot', handleUpdateSpotEvent);
    }
  });

  return {
    spot: currentSpot,
    spotCode,
    areaId,
    isLoading,
    refresh,
    onIntersect: handleIntersect,
    needsFetch
  };
}
